import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import ResizablePanel from '../../components/Resizeable/ResizablePanel';
import { DownloadButton } from '../../components/DownloadButton';
import {
  getFields,
  generateCsv as csv,
  generateReport as pdf,
  generateShapefile as shapefile,
} from '../../services/soil-report-service';
import Map from '../map/RxGoogleMap';
import { Table } from '../../components/table/Table';
import { Section } from '../Section';
import { useGrowers } from '../../hooks/use-growers';
import { useFields } from '../../hooks/use-fields';
import { ChooseInterpolations } from './ChooseInterpolations';
import { useFeatureFlags } from '../../hooks/use-feature-flags';

const layers = [
  { id: 'boron', name: 'Boron' },
  { id: 'calcium', name: 'Calcium' },
  { id: 'calcium_saturation', name: 'Calcium Saturation' },
  { id: 'copper', name: 'Copper' },
  { id: 'hydrogen_saturation', name: 'Hydrogen Saturation' },
  { id: 'iron', name: 'Iron' },
  { id: 'k_saturation', name: 'K Saturation' },
  { id: 'magnesium', name: 'Magnesium' },
  { id: 'magnesium_saturation', name: 'Magnesium Saturation' },
  { id: 'manganese', name: 'Manganese' },
  { id: 'sulfur', name: 'Sulfur' },
  { id: 'zinc', name: 'Zinc' },
];

export default function SoilReport() {
  const { selected: grower } = useGrowers();
  const [fields, setFields] = useState();
  const [selectionOptions, setSelectionOptions] = useState();
  const { selectedAaids } = useFields();
  const actionsApi = useRef();
  const flags = useFeatureFlags();

  const selectedFields = useMemo(
    () =>
      fields
        ? selectedAaids.map((aaid) => fields.find((f) => f.aaid === aaid))
        : [],
    [selectedAaids, fields],
  );

  const selectedActiveInterpolations = useMemo(
    () => selectedFields.flatMap((f) => f.interpolations.slice(0, 1)),
    [selectedFields],
  );

  const selectedInterpolations = useMemo(
    () => selectedFields.flatMap((f) => f.interpolations),
    [selectedFields],
  );

  const shouldSelectInterpolations = useMemo(
    () => selectedInterpolations.length > selectedActiveInterpolations.length,
    [selectedActiveInterpolations.length, selectedInterpolations.length],
  );

  const selectInterpolations = useCallback(
    (generator, setProgress) =>
      new Promise((resolve, reject) => {
        setSelectionOptions({ resolve, reject, generator, setProgress });
      }),
    [],
  );

  const generate = useCallback(
    (generator) => (setProgress) =>
      shouldSelectInterpolations
        ? selectInterpolations(generator, setProgress)
        : generator(grower, selectedActiveInterpolations, setProgress, flags),
    [
      flags,
      grower,
      selectInterpolations,
      selectedActiveInterpolations,
      shouldSelectInterpolations,
    ],
  );

  useEffect(() => {
    setFields(null);
    if (grower) getFields(grower.guid).then(convertToHierarchy).then(setFields);
  }, [grower]);

  const actions = useMemo(
    () =>
      [
        <DownloadButton
          key="csv"
          keep-open="true"
          disabled={!selectedAaids.length}
          download={generate(csv)}
        >
          Export CSV
        </DownloadButton>,
        <DownloadButton
          key="shapefile"
          keep-open="true"
          disabled={!selectedAaids.length}
          download={generate(shapefile)}
        >
          Export Results Shapefile
        </DownloadButton>,
        <DownloadButton
          key="pdf"
          keep-open="true"
          disabled={!selectedAaids?.length}
          download={generate(pdf)}
        >
          Print to PDF
        </DownloadButton>,
      ].filter(Boolean),
    [generate, selectedAaids],
  );

  return (
    <>
      {buildInterpolationChooser()}
      <Section>
        <ResizablePanel
          topPanel={<Map layers={layers} />}
          bottomPanel={buildBottomPanel()}
        />
      </Section>
    </>
  );

  function cancelChoose() {
    selectionOptions.resolve(null);
    setSelectionOptions(null);
    actionsApi.current.setOpen(true);
  }

  function saveChoose(chosen) {
    const { generator, setProgress, resolve, reject } = selectionOptions;
    generator(grower, chosen, setProgress, flags).then(resolve).catch(reject);
    setSelectionOptions(null);
    actionsApi.current.setOpen(true);
  }

  function buildInterpolationChooser() {
    if (!selectionOptions) return null;

    return (
      <ChooseInterpolations
        type={selectionOptions.generator === pdf ? 'pdf' : 'csv'}
        active={selectedActiveInterpolations}
        inactive={selectedInterpolations}
        onCancel={cancelChoose}
        onSave={saveChoose}
      />
    );
  }

  function buildBottomPanel() {
    return (
      <Box sx={{ height: '100%' }}>
        <Table
          actionsRef={actionsApi}
          loading={!fields}
          actions={actions}
          fields={fields || []}
          getRowId={(row) => row.id || row.aaid}
          isRowSelectable={({ row }) => Boolean(row.interpolationId)}
          treeData
          getTreeDataPath={(row) => row.hierarchy}
          sx={{
            '& .MuiDataGrid-row.historical': {
              color: '#777',
            },
            '& .MuiDataGrid-row.historical .MuiCheckbox-root': {
              display: 'none',
            },
          }}
          groupingColDef={{
            headerName: '',
            width: 50,
            valueFormatter: () => '',
          }}
          getRowClassName={({ row }) =>
            row.hierarchy?.length > 1 ? 'historical' : ''
          }
          columns={[
            {
              field: 'labReportId',
              headerName: 'Lab Report ID',
              flex: 1,
              headerAlign: 'left',
              align: 'left',
              valueFormatter: ({ value }) => value || '',
            },
            {
              field: 'sampleDate',
              headerName: 'Sample Date',
              type: 'date',
              flex: 0.75,
              headerAlign: 'right',
              align: 'right',
              valueFormatter: ({ value }) =>
                value &&
                new Intl.DateTimeFormat('en-US', { timeZone: 'UTC' }).format(
                  new Date(value),
                ),
            },
          ]}
        />
      </Box>
    );
  }
}

function convertToHierarchy(fields) {
  return fields.flatMap(mapFieldToHierarchy);
}

function mapFieldToHierarchy(field) {
  const children = (field.interpolations || []).slice(1).map((i) => ({
    id: i.id,
    aaid: field.aaid,
    fieldName: field.fieldName,
    acres: field.acres,
    farmName: field.farmName,
    fieldId: field.fieldId,
    sampleDate: i.sampleDate,
    labReportId: i.labReportId,
    hierarchy: [field.aaid, i.id],
  }));

  return [{ ...field, hierarchy: [field.aaid] }, ...children];
}
